import { useCallback, useEffect, useMemo, useState } from 'react'
import fee_rules_api from '../api/admin/fee_rules'
import val_api from '../api/valuation'
import GenericDialog from '../components/GenericDialog'
import { fmtMoney } from '../components/finviseMetrics'
import '../styles/finvise-portal.css'

const displayReportLengthName = name => {
	if (/super\s*light/i.test(name || '')) return 'Super Light'
	if (/^short/i.test(name || '')) return 'Short'
	if (/^long/i.test(name || '')) return 'Long'
	return name || ''
}

const displayPropertyTypeName = name => {
	if (/mufb/i.test(name || '')) return 'MUFB'
	if (/hmo/i.test(name || '')) return 'HMO'
	return 'SFR'
}

const fmtNumber = raw => {
	const n = Number(raw)
	return isFinite(n) ? n.toLocaleString('en-GB') : raw
}

const fmtBandLabel = (from, to) => {
	if (to === 'inf' || to == null) return `${fmtNumber(from)}+`
	return `${fmtNumber(from)} - ${fmtNumber(to)}`
}

const makeRowId = () => `row-${Date.now()}-${Math.random().toString(36).slice(2)}`

const feeIsNumeric = fee =>
	fee != null &&
	String(fee).trim() !== '' &&
	/^[0-9]+(\.[0-9]+)?$/.test(String(fee).trim())

const displayFee = fee =>
	feeIsNumeric(fee) ? fmtMoney(Number(fee)) : 'on request'

const cellKey = (reportLengthId, propertyTypeId) =>
	`${reportLengthId}:${propertyTypeId}`

const buildColumns = (groups, reportLengths, propertyTypes) => {
	const rlById = new Map(reportLengths.map(r => [r.id, r]))
	const ptById = new Map(propertyTypes.map(p => [p.id, p]))
	const seen = new Map()
	groups.forEach(g => {
		if (!seen.has(g.report_length_id)) seen.set(g.report_length_id, new Set())
		;(g.rules || []).forEach(r =>
			seen.get(g.report_length_id).add(r.property_type_id),
		)
	})
	return [...seen.keys()]
		.sort((a, b) => a - b)
		.map(rlId => ({
			reportLengthId: rlId,
			label: displayReportLengthName(rlById.get(rlId) && rlById.get(rlId).name),
			propertyTypes: [...seen.get(rlId)]
				.sort((a, b) => a - b)
				.map(ptId => ({
					propertyTypeId: ptId,
					label: displayPropertyTypeName(
						ptById.get(ptId) && ptById.get(ptId).name,
					),
				})),
		}))
}

const buildRows = groups => {
	const byBand = new Map()
	groups.forEach(g => {
		const key = `${g.value_from}|${g.value_to}`
		if (!byBand.has(key)) {
			byBand.set(key, {
				id: makeRowId(),
				valueFrom: g.value_from,
				valueTo: g.value_to,
				cells: new Map(),
			})
		}
		const row = byBand.get(key)
		;(g.rules || []).forEach(r => {
			row.cells.set(cellKey(g.report_length_id, r.property_type_id), r.fee)
		})
	})
	return [...byBand.values()].sort(
		(a, b) => Number(a.valueFrom) - Number(b.valueFrom),
	)
}

const rowsToPayload = (rows, columns) => {
	const payload = []
	rows
		.filter(row => String(row.valueFrom).trim() !== '')
		.forEach(row => {
		columns.forEach(col => {
			payload.push({
				report_length_id: col.reportLengthId,
				value_from: row.valueFrom,
				value_to: row.valueTo,
				rules: col.propertyTypes.map(pt => ({
					property_type_id: pt.propertyTypeId,
					fee:
						row.cells.get(cellKey(col.reportLengthId, pt.propertyTypeId)) ?? '',
				})),
			})
		})
	})
	return payload
}

const FinviseFeeRules = ({ orgId = null } = {}) => {
	const [reportLengths, setReportLengths] = useState([])
	const [propertyTypes, setPropertyTypes] = useState([])
	const [groups, setGroups] = useState([])
	const [rows, setRows] = useState([])
	const [isDefault, setIsDefault] = useState(false)
	const [loading, setLoading] = useState(false)
	const [editing, setEditing] = useState(false)
	const [saving, setSaving] = useState(false)
	const [showConfirm, setShowConfirm] = useState(false)
	const [errorTitle, setErrorTitle] = useState('')
	const [errorMessage, setErrorMessage] = useState('')

	useEffect(() => {
		let alive = true
		Promise.all([
			val_api.getValuationReportLength(),
			val_api.getValuationPropertyTypes(1),
		])
			.then(([rl, pt]) => {
				if (!alive) return
				setReportLengths((rl.data && rl.data.types) || [])
				setPropertyTypes((pt.data && pt.data.types) || [])
			})
			.catch(() => {})
		return () => {
			alive = false
		}
	}, [])

	const loadRules = useCallback(() => {
		if (orgId == null) return
		setLoading(true)
		fee_rules_api
			.getFeeRules(orgId)
			.then(res => {
				const orgRules = (res.data && res.data.rules) || []
				if (orgRules.length > 0) {
					setGroups(orgRules)
					setIsDefault(false)
					return null
				}
				return fee_rules_api.getDefaultFeeRules().then(defRes => {
					setGroups((defRes.data && defRes.data.rules) || [])
					setIsDefault(true)
				})
			})
			.catch(() => {
				setGroups([])
			})
			.finally(() => setLoading(false))
	}, [orgId])

	useEffect(() => {
		loadRules()
	}, [loadRules])

	const columns = useMemo(
		() => buildColumns(groups, reportLengths, propertyTypes),
		[groups, reportLengths, propertyTypes],
	)

	useEffect(() => {
		setRows(buildRows(groups))
	}, [groups])

	const startEdit = () => setEditing(true)
	const cancelEdit = () => {
		setRows(buildRows(groups))
		setEditing(false)
	}

	const updateBand = (rowIdx, field, value) => {
		setRows(prev =>
			prev.map((row, i) => (i === rowIdx ? { ...row, [field]: value } : row)),
		)
	}

	const updateFee = (rowIdx, reportLengthId, propertyTypeId, value) => {
		setRows(prev =>
			prev.map((row, i) => {
				if (i !== rowIdx) return row
				const cells = new Map(row.cells)
				cells.set(cellKey(reportLengthId, propertyTypeId), value)
				return { ...row, cells }
			}),
		)
	}

	const addRow = () => {
		setRows(prev => [
			...prev,
			{ id: makeRowId(), valueFrom: '', valueTo: '', cells: new Map() },
		])
	}

	const removeRow = rowId => {
		setRows(prev => prev.filter(row => row.id !== rowId))
	}

	const askSave = () => setShowConfirm(true)

	const confirmSave = () => {
		setShowConfirm(false)
		setSaving(true)
		const payload = rowsToPayload(rows, columns)
		fee_rules_api
			.saveFeeRules(orgId, payload)
			.then(() => {
				setEditing(false)
				loadRules()
			})
			.catch(err => {
				console.error('[fee-rules] save failed', err.response || err)
				const status = err.response ? err.response.status : null
				setErrorTitle('Could not save')
				setErrorMessage(
					'Something went wrong saving the fees schedule' +
						(status ? ` (HTTP ${status})` : '') +
						'. Check the browser console / server logs for details.',
				)
			})
			.finally(() => setSaving(false))
	}

	const totalCols = 1 + columns.reduce((n, c) => n + c.propertyTypes.length, 0)

	return (
		<>
			<div className='finvise-portal'>
				<div className='wrap'>
					<div className='page-head'>
						<div>
							<h1>Fees Schedule</h1>
							<div className='sub'>Price per RICS report, excluding VAT</div>
						</div>
						<div className='head-actions'>
							{editing ? (
								<>
									<button
										className='action-btn'
										onClick={cancelEdit}
										disabled={saving}
									>
										Cancel
									</button>
									<button
										className='action-btn primary'
										onClick={askSave}
										disabled={saving}
									>
										Save
									</button>
								</>
							) : (
								<button
									className='action-btn'
									onClick={startEdit}
									disabled={loading}
								>
									Edit
								</button>
							)}
						</div>
					</div>

					{isDefault ? (
						<div className='fee-rules-note'>
							<svg
								width='14'
								height='14'
								viewBox='0 0 24 24'
								fill='none'
								stroke='currentColor'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
							>
								<path d='M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
							</svg>
							This organisation has no custom pricing yet — showing the default
							rates. Saving will create pricing specific to this organisation.
						</div>
					) : null}

					<div className='card table-card'>
						{loading ? (
							<div className='card-loading'>
								<span className='spinner' />
							</div>
						) : null}
						<div className='table-scroll'>
							<table className='fee-rules-table'>
								<colgroup>
									<col style={{ width: 250 }} />
									{columns.map(col =>
										col.propertyTypes.map(pt => (
											<col key={cellKey(col.reportLengthId, pt.propertyTypeId)} />
										)),
									)}
								</colgroup>
								<thead>
									<tr className='grp-row'>
										<th className='band-head'></th>
										{columns.map((col, ci) => (
											<th
												key={col.reportLengthId}
												colSpan={col.propertyTypes.length}
												className={
													ci < columns.length - 1 ? 'grp-gap' : undefined
												}
											>
												{col.label}
											</th>
										))}
									</tr>
									<tr className='sub-row'>
										<th>Property value</th>
										{columns.map((col, ci) =>
											col.propertyTypes.map((pt, pi) => (
												<th
													key={cellKey(col.reportLengthId, pt.propertyTypeId)}
													className={
														pi === col.propertyTypes.length - 1 &&
														ci < columns.length - 1
															? 'grp-gap'
															: undefined
													}
												>
													{pt.label}
												</th>
											)),
										)}
									</tr>
								</thead>
								<tbody>
									{rows.length === 0 ? (
										<tr>
											<td colSpan={totalCols} className='empty-state'>
												{loading ? 'Loading…' : 'No fees schedule configured'}
											</td>
										</tr>
									) : (
										rows.map((row, ri) => (
											<tr key={row.id}>
												<td className='band'>
													{editing ? (
														<div className='band-range'>
															<input
																className='band-range-input'
																value={row.valueFrom}
																onChange={e =>
																	updateBand(ri, 'valueFrom', e.target.value)
																}
															/>
															<span className='dash'>&ndash;</span>
															<input
																className='band-range-input'
																value={row.valueTo}
																onChange={e =>
																	updateBand(ri, 'valueTo', e.target.value)
																}
															/>
															<button
																type='button'
																className='band-remove'
																onClick={() => removeRow(row.id)}
																title='Remove this price band'
																aria-label='Remove this price band'
															>
																<svg
																	width='11'
																	height='11'
																	viewBox='0 0 24 24'
																	fill='none'
																	stroke='currentColor'
																	strokeWidth='2.5'
																	strokeLinecap='round'
																>
																	<path d='M6 6l12 12M18 6L6 18' />
																</svg>
															</button>
														</div>
													) : (
														fmtBandLabel(row.valueFrom, row.valueTo)
													)}
												</td>
												{columns.map((col, ci) =>
													col.propertyTypes.map((pt, pi) => {
														const key = cellKey(
															col.reportLengthId,
															pt.propertyTypeId,
														)
														const fee = row.cells.get(key)
														const gapCls =
															pi === col.propertyTypes.length - 1 &&
															ci < columns.length - 1
																? 'grp-gap'
																: undefined
														return (
															<td key={key} className={gapCls}>
																{editing ? (
																	<input
																		className='fee-cell-input'
																		placeholder='on request'
																		value={fee ?? ''}
																		onChange={e =>
																			updateFee(
																				ri,
																				col.reportLengthId,
																				pt.propertyTypeId,
																				e.target.value,
																			)
																		}
																	/>
																) : feeIsNumeric(fee) ? (
																	displayFee(fee)
																) : (
																	<span className='on-request'>on request</span>
																)}
															</td>
														)
													}),
												)}
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>
					</div>

					{editing ? (
						<div className='fee-rules-add-row'>
							<button type='button' className='action-btn' onClick={addRow}>
								+ Add price band
							</button>
						</div>
					) : null}

					<div className='fee-rules-footnote'>
						<div>*</div>
						<div>AVM: £5 </div>
						<div>Site visit: £150 </div>
					</div>
				</div>
			</div>

			{/* Rendered outside .finvise-portal — that wrapper resets margin/padding on
			    every descendant, which would collapse GenericDialog's real spacing. */}
			{showConfirm ? (
				<GenericDialog
					title='Save changes?'
					text="You're about to update the fees schedule for this organisation. New valuations will use these prices immediately."
					buttons={[
						{ text: 'Cancel', id: 'cancel' },
						{ text: 'Confirm', id: 'confirm' },
					]}
					result={id => {
						if (id === 'confirm') confirmSave()
						else setShowConfirm(false)
					}}
				/>
			) : null}

			{errorMessage && errorTitle ? (
				<GenericDialog
					title={errorTitle}
					text={errorMessage}
					buttons={[{ text: 'OK', id: 'ok' }]}
					result={() => {
						setErrorTitle('')
						setErrorMessage('')
					}}
				/>
			) : null}
		</>
	)
}

export default FinviseFeeRules
